import { PaymentMethod, PaymentReason, PaymentStatus } from '@apply/constants';
import { ApiResponse, BfiApi, IApiResponse, SearchResponse } from './bfiApi';

export interface AddressVerificationRequest {
    paymentGuid: string;
    cardNumber: string;
    creditCardType: 'VISA' | 'MASTERCARD';
    expirationDate: string;
    creditDebitType: string;
    address: string;
    zipCode: string;
    amount: number;
}

export interface AuthorizationResponse extends Omit<IApiResponse, 'result'> {
    result: {
        isSuccess: boolean;
        errorMessage: string;
        avsResponseCode: string;
    };
}

export interface AuthorizationRequest {
    paymentGuid: string;
    avsAuthResponseCode: string;
    accountNumber: string;
    name: string;
    phone: string;
    email: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    cardType: 'VISA' | 'MASTERCARD';
    expirationDate: string;
    amount: number;
    paymentHistoryType: number;
    memo: string;
    applicationId?: string;
    ipAddress: string;
    disclaimer: string;
    paymentReason: PaymentReason;
}

export interface RefundRequest {
    paymentId: number;
    amount: number;
}

export interface BillingDetails {
    address: {
        city: string;
        country: string;
        line1: string;
        line2: string;
        postal_code: string;
        state: string;
    };
    email: string;
    name: string;
    phone: string;
}

export interface Payment {
    paymentId: number;
    paymentGuid: string;
    amount: number;
    paymentStatus: PaymentStatus;
    paymentMethod: PaymentMethod;
    paymentDate: Date;
    lastUpdate: Date;
    bfiNumber: string;
    customerName: string;
    cardLastFourDigits: number;
    expirationDate: string;
    memo: string;
    phone: string;
    email: string;
    address: string;
    zipCode: string;

    histories: PaymentHistory[];
    refunds: PaymentRefund[];
}

export interface PaymentGridRow extends Payment {
    searchText: string;
    take: number;
    skip: number;
}

export interface GetPaymentGridRequest {
    startDate: Date;
    endDate: Date;
    paymentStatus: PaymentStatus | undefined;
}

export interface SetBfiNumberRequest {
    paymentId: number;
    bfiNumber: string;
}

export interface PaymentHistory {
    paymentStatus: PaymentStatus;
    note: string;
    noteDate: Date;
}

export interface PaymentRefund {
    paymentId: number;
    amount: number;
}

export interface CountByPaymentType {
    paymentStatus: PaymentStatus;
    count: number;
}

export interface TopCustomers {
    name: string;
    email: string;
    total: number;
}

export interface PaymentFailures {
    name: string;
    email: string;
    phone: number;
    amount: number;
    note: string;
}

export interface MonthVsMonthReportEntry {
    monthDate: string;
    totalA: number;
    totalB: number;
  }

export interface MonthVsMonthPayments {
    monthA: string;
    monthB: string;
    reportData: MonthVsMonthReportEntry[];
}

export class PaymentApi extends BfiApi {
    verifyAddress(data: AddressVerificationRequest): Promise<AuthorizationResponse> {
        return super.post('/payment/verifyAddress', data);
    }

    authorization(data: AuthorizationRequest): Promise<AuthorizationResponse> {
        return super.post('/payment/authorization', data);
    }

    refund(data: RefundRequest): Promise<ApiResponse<Payment>> {
        return super.post('/payment/refund', data);
    }

    setBfiNumber(data: SetBfiNumberRequest): Promise<ApiResponse<undefined>> {
        return super.post('/payment/setBfiNumber', data);
    }

    getPaymentGrid(data: GetPaymentGridRequest): Promise<SearchResponse<PaymentGridRow[]>> {
        return super.post('/payment/getPaymentGrid', {
            startDate: data.startDate,
            endDate: data.endDate,
            paymentStatus: data.paymentStatus,
        });
    }

    report_CountByPaymentType(days: number): Promise<ApiResponse<CountByPaymentType[]>> {
        return super.get(`/payment/report/countByPaymentType/${days}`);
    }

    report_TopCustomers(days: number): Promise<ApiResponse<TopCustomers[]>> {
        return super.get(`/payment/report/topCustomers/${days}`);
    }

    report_PaymentFailures(days: number): Promise<ApiResponse<PaymentFailures[]>> {
        return super.get(`/payment/report/failureList/${days}`);
    }

    report_MonthVsMonthPayments(): Promise<ApiResponse<MonthVsMonthPayments>> {
        return super.get(`/payment/report/monthVsMonth`);
    }
}
